import React, { useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import CardMedia from "@mui/material/CardMedia";
import Typography from "@mui/material/Typography";
import CircularProgress from "@mui/material/CircularProgress";
import { motion } from "framer-motion";
import api from "../services/httpService";
import "./OurClient.css"; 

/* ================= ANIMATIONS (UNCHANGED) ================= */
const cardVariants = {
  hidden: { opacity: 0, y: 30, scale: 0.95 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    scale: 1,
    transition: { delay: i * 0.15, duration: 0.6 },
  }),
};

const hoverVariants = {
  hover: {
    scale: 1.05,
    y: -10,
    boxShadow: "0 8px 30px rgba(0,95,163,0.3)",
    transition: { duration: 0.3 },
  },
};

const imageVariants = {
  hover: {
    scale: 1.1,
    transition: { duration: 0.4 },
  },
};

const contentVariants = {
  hover: {
    backgroundColor: "#f8fdff",
    transition: { duration: 0.3 },
  },
};

const OurClient = () => {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  // const [themeColors, setThemeColors] = useState({
  //   color1: "#f83600",
  //   color2: "#fe8c00"
  // });

  /* ================= FETCH FROM ADMIN API ================= */
  useEffect(() => {
    fetchClients();
    fetchThemeColors();
  }, []);

  const fetchThemeColors = async () => {
    try {
      const res = await api.get("/sentinix/dashboardsettings");
      if (res.data.success && res.data.data) {
        const allData = Array.isArray(res.data.data) ? res.data.data : [res.data.data];
        const latest = allData.sort((a, b) => b.id - a.id)[0];

        if (latest) {
          const color1 = latest.color1 || "#f83600";
          const color2 = latest.color2 || "#fe8c00";
          
          // setThemeColors({ color1, color2 });
          
          // Apply to CSS variables
          document.documentElement.style.setProperty("--theme-color1", color1);
          document.documentElement.style.setProperty("--theme-color2", color2);
        }
      }
    } catch (err) {
      console.error("Error fetching theme colors:", err);
    }
  };

  const fetchClients = async () => {
    try {
      const res = await api.get("/sentinix/clients");
      if (res.data.success) {
        const raw = res.data.data;
        const list = Array.isArray(raw) ? raw : raw ? [raw] : [];
        const filtered = list.filter((c) => !c.isDeleted);
        setClients(filtered);
      }
    } catch (err) {
      console.error("Error loading clients", err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        px: { xs: 2, md: 6 },
        py: 8,
        background: "linear-gradient(135deg, #f5f9ff 0%, #ffffff 100%)",
      }}
    >
     {/* TITLE */}
<motion.div
  initial={{ opacity: 0, y: -20 }}
  whileInView={{ opacity: 1, y: 0 }}
  viewport={{ once: true }}
  transition={{ duration: 0.6 }}
>
  <Box
    sx={{
      width: "100%",
      display: "flex",
      justifyContent: "center",
      textAlign: "center",
      mb: 4,
    }}
  >
    <Typography
      variant="h3"
      fontWeight="bold"
      className="theme-gradient-text"
      sx={{
        fontSize: { xs: "2rem", md: "3rem" },
        textShadow: "0 2px 4px rgba(0,0,0,0.1)",
      }}
    >
      Our Clients
    </Typography>
  </Box>
</motion.div>

      {/* LOADING */}
      {loading ? (
        <Box textAlign="center" py={6}>
          <CircularProgress className="theme-spinner" />
          <Typography mt={2}>Loading clients...</Typography>
        </Box>
      ) : (
        <Grid container spacing={4} justifyContent="center">
          {clients.map((client, index) => {
            const descriptionLines = client.description
              ? client.description.split("\n")
              : [];

            return (
              <Grid item xs={12} sm={6} md={4} key={client.id}>
                <motion.div
                  custom={index}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ amount: 0.1 }}
                  variants={cardVariants}
                  whileHover="hover"
                >
                  <motion.div variants={hoverVariants} style={{ height: "100%" }}>
                    <Card
                      className="client-card"
                      sx={{
                        maxWidth: 345,
                        margin: "auto",
                        display: "flex",
                        flexDirection: "column",
                        height: "100%",
                        borderRadius: 3,
                        overflow: "hidden",
                        cursor: "pointer",
                        position: "relative",
                      }}
                    >
                      <div className="card-top-bar" />
                      
                      <motion.div variants={imageVariants}>
                        <CardMedia
                          component="img"
                          height="200"
                          image={
                            client.ImageKey ||
                            "https://via.placeholder.com/400x200?text=No+Image"
                          }
                          alt={client.name}
                          sx={{ objectFit: "cover" }}
                        />
                      </motion.div>

                      <motion.div variants={contentVariants}>
                        <CardContent sx={{ padding: "1.5rem" }}>
                          <Typography
                            variant="h6"
                            className="client-name theme-gradient-text"
                            sx={{
                              fontWeight: "bold",
                              mb: 2,
                            }}
                          >
                            {client.name}
                          </Typography>

                          <Box sx={{ maxHeight: 300, overflow: "auto" }}>
                            {descriptionLines.map((line, idx) => (
                              <Typography
                                key={idx}
                                variant="body2"
                                color="text.secondary"
                                sx={{ mb: 1.5, lineHeight: 1.6 }}
                              >
                                {line}
                              </Typography>
                            ))}
                          </Box>
                        </CardContent>
                      </motion.div>
                    </Card>
                  </motion.div>
                </motion.div>
              </Grid>
            );
          })}
        </Grid>
      )}
    </Box>
  );
};

export default OurClient;